import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { changePassword } from "../services/api";
// [FORGOT-PASSWORD FEATURE - DEMO] remove this line to roll back
import ForgotPasswordModal from "./ForgotPasswordModal";

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

/* Pull the most useful message out of a DRF error response */
function extractError(err) {
  const data = err.response?.data;
  if (!data) return "Could not reach the server. Please try again.";
  if (typeof data === "string") return "Something went wrong. Please try again.";
  const first =
    data.current_password?.[0] ||
    data.new_password?.[0] ||
    data.non_field_errors?.[0] ||
    data.detail;
  return first || "Could not update password. Please try again.";
}

export default function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  // [FORGOT-PASSWORD FEATURE - DEMO] remove this line to roll back
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.next !== form.confirm) {
      setError("New password and confirm password do not match.");
      return;
    }
    if (form.next === form.current) {
      setError("New password must be different from your current password.");
      return;
    }

    setSaving(true);
    try {
      await changePassword({
        current_password: form.current,
        new_password: form.next,
      });
      setDone(true);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  // Rendered into <body> via a portal: the navbar uses backdrop-filter, which
  // would otherwise trap this fixed-position modal inside the 64px header.
  return createPortal(
    <div className="modal-backdrop" onClick={() => !saving && onClose()}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Change password"
        style={{ maxWidth: 380, textAlign: "left" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose} disabled={saving} aria-label="Close">
          <CloseIcon />
        </button>
        <div className="modal-title" style={{ marginBottom: 16 }}>Change Password</div>

        {done ? (
          <div>
            <p style={{ color: "#16A34A", marginBottom: 16 }}>
              Your password has been updated successfully.
            </p>
            <button className="btn btn-secondary" style={{ width: "100%" }} onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input
                type="password"
                className="form-input"
                value={form.current}
                onChange={update("current")}
                autoComplete="current-password"
                autoFocus
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-input"
                value={form.next}
                onChange={update("next")}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                className="form-input"
                value={form.confirm}
                onChange={update("confirm")}
                autoComplete="new-password"
                required
              />
            </div>

            {error && <div className="error-text">{error}</div>}

            <button className="btn btn-primary" style={{ width: "100%", marginTop: 8 }} disabled={saving}>
              {saving ? "Updating..." : "Update Password"}
            </button>

            {/* [FORGOT-PASSWORD FEATURE - DEMO] remove this block to roll back */}
            <p style={{ fontSize: 13, marginTop: 12, textAlign: "center" }}>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                style={{ background: "none", border: "none", color: "var(--myg-red, #E31E24)", cursor: "pointer", padding: 0, font: "inherit", textDecoration: "underline" }}
              >
                Forgot your current password?
              </button>
            </p>
          </form>
        )}
      </div>
      {/* [FORGOT-PASSWORD FEATURE - DEMO] remove this block to roll back */}
      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
    </div>,
    document.body
  );
}
